import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import Popover from 'components/molecules/Popover'
import { accessibleOnClick } from 'utils'
import notify from 'components/atoms/Notify'
import { IoTrashBin, IoPencil, IoCheckmark, IoClose, IoSearch, IoCalendar, IoAdd } from 'react-icons/io5'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

import useModal from 'hooks/useModal'
import Discount from './modals/DiscountModal'
import apiDelete from 'lib/network/apiDelete'
import { DateRangePicker } from 'react-date-range'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import PageFooter from 'components/paginationComponent'

const Discounts = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [filter, setFilter] = useState({ workspace: -1 })
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })

  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [discounts, setDiscounts] = useState([])
  const [selectedDiscountId, setSelectedDiscountId] = useState(null)
  const [search, setSearch] = useState('')

  const popover = useRef()

  const fetchDiscounts = async (page = 1) => {
    setLoading(true)

    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    params.query = search || undefined
    params.startDate = dateRange.startDate ? dateRange.startDate.format('MM-DD-YYYY') : undefined
    params.endDate = dateRange.endDate ? dateRange.endDate.format('MM-DD-YYYY') : undefined
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }
    const response = await apiGet('discounts', params)
    setDiscounts(page === 1 ? response.data.discount : [...discounts, ...response.data.discount])
    setPagination(response?.data?.pagination || {})
    setLoading(false)
  }

  useEffect(() => {
    fetchDiscounts()
  }, [search, dateRange, filter])

  const deleteDiscount = id => {
    notify.deleteConfirm({
      title: t('discounts.modals.deleteCoupon'),
      message: t('discounts.modals.deleteConfirm'),
      onConfirm: async () => {
        const response = await apiDelete(`discount/${id}`)
        if (response?.success) {
          setDiscounts(prevDiscounts => prevDiscounts.filter(discount => discount._id !== id))
          notify.success(t('discounts.messages.discountDeletedSuccess'))
        }
      },
    })
  }

  const handleAddDiscountSuccess = newDiscount => {
    fetchDiscounts()
    closeModal()
  }

  const clearFilters = () => {
    setSearch('')
    setDateRange({ startDate: null, endDate: null })
  }

  const hasActiveFilters = search || dateRange.startDate || dateRange.endDate

  const [DiscountModal, openDiscountModal, closeModal] = useModal({
    content: <Discount onSuccess={handleAddDiscountSuccess} id={selectedDiscountId} />,
    title: t('discounts.modals.addDiscountCoupons'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '700px',
      minHeight: 'auto',
      minWidth: '480px',
    },
  })

  const handleSelect = range => {
    setDateRange({
      startDate: moment(range.selection.startDate),
      endDate: moment(range.selection.endDate),
    })
  }

  const columns = [
    {
      name: t('discounts.columns.name'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        return (
          <div className="ml-4">
            <div className="text-sm text-normal text-gray-900">{item?.cupon}</div>
          </div>
        )
      },
    },

    {
      width: '10%',
      name: t('discounts.columns.type'),
      key: 'type',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.type === 'Flat' ? t('discounts.types.flat') : t('discounts.types.percentage')}</span>
            </div>
          </>
        )
      },
    },

    {
      width: '5%',
      name: t('discounts.columns.startDate'),
      key: 'effectiveStartDate',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'effectiveStartDate', '')
        const createdAt = createdTime ? moment(createdTime).format('MMMM DD, YYYY') : 'Registered'
        return createdAt
      },
    },
    {
      width: '5%',
      name: t('discounts.columns.expireDate'),
      key: 'effectiveEndDate',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'effectiveEndDate', '')
        const createdAt = createdTime ? moment(createdTime).format('MMMM DD, YYYY') : 'Registered'
        return createdAt
      },
    },

    {
      width: '10%',
      name: t('discounts.columns.amount'),
      key: 'amount',
      sortable: true,
      rendered: item => {
        return item.type === 'Flat' ? (
          <div>
            <span className="text-[13px]">{item.amount}</span>
          </div>
        ) : null
      },
    },
    {
      width: '10%',
      name: t('discounts.columns.percentage'),
      key: 'percentage',
      sortable: true,
      rendered: item => {
        return item.type === 'Percentage' ? (
          <div>
            <span className="text-[13px]">{item.percentage}</span>
          </div>
        ) : null
      },
    },
    {
      width: '10%',
      name: t('discounts.columns.isActive'),
      key: 'status',
      sortable: true,
      rendered: item => {
        return (
          <div className="flex items-center">
            <span className="text-[13px]">{item.status ? <IoCheckmark className="text-green-500" /> : <IoClose className="text-red-500" />}</span>
          </div>
        )
      },
    },

    {
      width: '5%',
      name: t('discounts.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },

    {
      width: '5%',
      name: t('discounts.columns.actions'),
      key: 'actions',
      rendered: item => (
        <div className="flex space-x-2 sm:space-x-4">
          <IoPencil
            className="text-dark-600 cursor-pointer h-4 w-4 sm:h-5 sm:w-5"
            onClick={() => {
              setSelectedDiscountId(item._id)
              openDiscountModal()
            }}
          />
          <IoTrashBin className="text-red-500 cursor-pointer h-4 w-4 sm:h-5 sm:w-5" onClick={() => deleteDiscount(item._id)} />
        </div>
      ),
    },
  ]

  return (
    <Layout
      headerRight={
        <div className="w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-end gap-4">
            <div className="relative rounded-md shadow-sm flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className="block w-full pl-9 pr-3 py-2 shadow-none text-sm bg-gray-100 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white rounded-md transition-all"
                placeholder={t('discounts.messages.searchPlaceholder')}
              />
            </div>

            <div className="flex-shrink-0">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            </div>

            <div className="flex-shrink-0">
              <Popover
                custom
                placement="left"
                ref={popover}
                content={close => (
                  <div className="p-4">
                    <DateRangePicker
                      ranges={[
                        {
                          startDate: dateRange.startDate ? dateRange.startDate.toDate() : new Date(),
                          endDate: dateRange.endDate ? dateRange.endDate.toDate() : new Date(),
                          key: 'selection',
                        },
                      ]}
                      onChange={handleSelect}
                    />
                    <div className="flex justify-end mt-4 gap-2">
                      <button
                        onClick={close}
                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                      >
                        {t('discounts.buttons.close')}
                      </button>
                      <button
                        disabled={!dateRange.startDate && !dateRange.endDate}
                        onClick={() => {
                          setDateRange({ startDate: null, endDate: null })
                        }}
                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('discounts.buttons.clear')}
                      </button>
                    </div>
                  </div>
                )}
              >
                <button
                  type="button"
                  className="inline-flex w-[250px] h-[40px] justify-center items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none transition-all"
                >
                  <IoCalendar className="h-4 w-4 mr-3 text-gray-500" />
                  <span className="truncate">
                    {dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.format('MM-DD-YYYY')} to ${dateRange.endDate.format('MM-DD-YYYY')}`
                      : t('discounts.messages.selectDateRange')}
                  </span>
                </button>
              </Popover>
            </div>

            <div className="flex-shrink-0">
              <Button
                onClick={() => {
                  setSelectedDiscountId(null)
                  openDiscountModal()
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 transition-all"
              >
                <IoAdd className="h-4 w-4 mr-2" />
                {t('discounts.buttons.addDiscounts')}
              </Button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className="block w-full pl-10 pr-4 py-3 text-base bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all"
                placeholder={t('discounts.messages.searchPlaceholder')}
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <IoClose className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            {/* Date Range and Add Button Row */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Popover
                  custom
                  placement="bottom"
                  content={close => (
                    <div className="p-4">
                      <DateRangePicker
                        ranges={[
                          {
                            startDate: dateRange.startDate ? dateRange.startDate.toDate() : new Date(),
                            endDate: dateRange.endDate ? dateRange.endDate.toDate() : new Date(),
                            key: 'selection',
                          },
                        ]}
                        onChange={handleSelect}
                      />
                      <div className="flex justify-end mt-4 gap-2">
                        <button
                          onClick={close}
                          className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                        >
                          {t('discounts.buttons.close')}
                        </button>
                        <button
                          disabled={!dateRange.startDate && !dateRange.endDate}
                          onClick={() => {
                            setDateRange({ startDate: null, endDate: null })
                          }}
                          className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('discounts.buttons.clear')}
                        </button>
                      </div>
                    </div>
                  )}
                >
                  <button
                    type="button"
                    className="w-full inline-flex justify-center items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 outline-none transition-all"
                  >
                    <IoCalendar className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="truncate">
                      {dateRange.startDate && dateRange.endDate
                        ? `${dateRange.startDate.format('MM/DD/YY')} - ${dateRange.endDate.format('MM/DD/YY')}`
                        : t('discounts.messages.selectDateRange')}
                    </span>
                  </button>
                </Popover>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button onClick={clearFilters} className="px-3 py-3 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors">
                  <IoClose className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Add Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  setSelectedDiscountId(null)
                  openDiscountModal()
                }}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-dark-600 hover:bg-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 transition-all"
              >
                <IoAdd className="h-4 w-4 mr-2" />
                {t('discounts.buttons.addDiscounts')}
              </Button>
            </div>

            {/* Pagination */}
            <div className="flex justify-center">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            </div>
          </div>
        </div>
      }
      title={t('discounts.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {discounts?.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('discounts.messages.noDiscountsFound')}</h3>
                        <p className="text-gray-500">{t('discounts.messages.noDiscountsDescription')}</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table
                          hasMore={pagination?.currentPage !== pagination?.totalPages}
                          fetchData={() => fetchDiscounts(pagination?.currentPage + 1)}
                          sortBy={filter.sortBy}
                          sortOrder={filter.sortOrder}
                          handleSort={(sortBy, sortOrder) => {
                            setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                          }}
                          data={discounts}
                          columns={columns}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <DiscountModal />
    </Layout>
  )
}

export default Discounts
